import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAudioVideo, useLocalVideo, useMeetingManager } from 'amazon-chime-sdk-component-library-react'

import { useMeetingStore } from '@stores/meeting'
import { useDepartedParticipantsStore } from '@/stores/departedParticipants'
import useParticipantNotifications from '@hooks/useParticipantNotifications'
import { useParticipantJoinNotification } from '@hooks/useParticipantJoinNotification'
import { useDeviceSelection } from '@hooks/useDeviceSelection'
import { VideoMeetingDesktop } from './VideoMeetingDesktop'
import { VideoMeetingMobile } from './VideoMeetingMobile'
import { useNetworkConnectionAlert } from '@hooks/useNetworkConnectionAlert'
import { useBrowserPermissions } from '@/hooks'
import { MeetingDataProvider } from '@/contexts/MeetingDataContext'
import { LoadingState } from '@components/atoms/LoadingState/LoadingState'
import { NetworkConnectionAlert } from '@/components/molecules/NetworkConnectionAlert'
import { useMeetingData } from '@/contexts/MeetingDataContext'
import { useLeaveMeetingMutation } from '@/models/generated/graphql'

export function VideoMeetingPage() {
  const { meetingId: routeMeetingId } = useParams<{
    appointmentId?: string
    meetingId?: string
  }>()

  const meetingManager = useMeetingManager()
  const audioVideo = useAudioVideo()

  const { isVideoEnabled, toggleVideo } = useLocalVideo()

  const {
    meetingId: storeMeetingId,
    shouldStartVideo,
    setShouldStartVideo
  } = useMeetingStore((s) => ({
    meetingId: s.meetingId,
    shouldStartVideo: s.shouldStartVideo,
    setShouldStartVideo: s.setShouldStartVideo
  }))

  const effectiveMeetingId = storeMeetingId ?? routeMeetingId?.trim() ?? null

  const isMeetingReady = !!meetingManager.meetingSession
  const isMeetingConnected = !!audioVideo

  useEffect(() => {
    if (shouldStartVideo && !isVideoEnabled && isMeetingConnected) {
      void toggleVideo()
      setShouldStartVideo(false)
    }
  }, [shouldStartVideo, isVideoEnabled, isMeetingConnected, toggleVideo, setShouldStartVideo])

  if (!effectiveMeetingId) return null

  if (!isMeetingReady || !isMeetingConnected) {
    return <LoadingState message="Connexion à la réunion…" />
  }

  return (
    <MeetingDataProvider>
      <MeetingContent />
    </MeetingDataProvider>
  )
}

function MeetingContent() {
  useParticipantNotifications()
  const { selectedAudioOutput } = useDeviceSelection({ autoSelect: false })
  useParticipantJoinNotification(selectedAudioOutput)
  const { interlocutor } = useMeetingData()
  const { showAlert, dismissAlert } = useNetworkConnectionAlert(interlocutor)
  const clearDeparted = useDepartedParticipantsStore((s) => s.clearDeparted)
  const appointmentId = useMeetingStore((s) => s.appointmentId)
  const [leaveMeetingMutation] = useLeaveMeetingMutation()

  // Check browser permissions for camera and microphone
  const { camera, microphone } = useBrowserPermissions()
  const isCameraPermissionDenied = camera !== 'granted'
  const isMicrophonePermissionDenied = microphone !== 'granted'

  // Nettoyer le store des participants partis au démontage
  useEffect(() => {
    return () => {
      clearDeparted()
    }
  }, [clearDeparted])

  // Call leaveMeeting mutation when user closes the tab
  useEffect(() => {
    if (!appointmentId) return

    const handleBeforeUnload = () => {
      leaveMeetingMutation({
        variables: { appointmentId },
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore'
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [appointmentId])

  return (
    <>
      {interlocutor && (
        <NetworkConnectionAlert
          isOpen={showAlert}
          onClose={dismissAlert}
          interlocutor={interlocutor}
        />
      )}
      <VideoMeetingDesktop
        isCameraPermissionDenied={isCameraPermissionDenied}
        isMicrophonePermissionDenied={isMicrophonePermissionDenied}
      />
      <VideoMeetingMobile
        isCameraPermissionDenied={isCameraPermissionDenied}
        isMicrophonePermissionDenied={isMicrophonePermissionDenied}
      />
    </>
  )
}
